import {
  onDisconnect,
  onValue,
  ref,
  serverTimestamp,
  set,
  update,
  type Unsubscribe,
} from 'firebase/database';
import { getFirebaseDb } from '@/lib/firebase';
import type { AgentPresenceStatus, PresenceRecord } from '@/types/support';

const HEARTBEAT_MS = 20_000;

export async function startPresence(opts: {
  uid: string;
  role: PresenceRecord['role'];
  status?: AgentPresenceStatus;
  pageUrl?: string;
  requestId?: string | null;
}): Promise<() => void> {
  const db = getFirebaseDb();
  const presenceRef = ref(db, `presence/${opts.uid}`);

  const writeOnline = async () => {
    const payload: Record<string, unknown> = {
      uid: opts.uid,
      online: true,
      lastSeen: serverTimestamp(),
      role: opts.role,
      status: opts.status ?? (opts.role === 'customer' ? 'online' : 'online'),
      pageUrl: opts.pageUrl ?? null,
      requestId: opts.requestId ?? null,
    };
    await set(presenceRef, payload);
  };

  await writeOnline();
  await onDisconnect(presenceRef).update({
    online: false,
    lastSeen: serverTimestamp(),
    status: 'offline',
  });

  const interval = window.setInterval(() => {
    void update(presenceRef, {
      online: true,
      lastSeen: serverTimestamp(),
      pageUrl: opts.pageUrl ?? null,
      requestId: opts.requestId ?? null,
      status: opts.status ?? 'online',
    });
  }, HEARTBEAT_MS);

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      void update(presenceRef, { online: true, lastSeen: serverTimestamp(), status: opts.status ?? 'online' });
    } else {
      void update(presenceRef, { lastSeen: serverTimestamp(), status: opts.role === 'agent' ? 'idle' : 'online' });
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisibility);
    void update(presenceRef, { online: false, lastSeen: Date.now(), status: 'offline' });
  };
}

export function updatePresenceStatus(uid: string, status: AgentPresenceStatus): Promise<void> {
  return update(ref(getFirebaseDb(), `presence/${uid}`), {
    status,
    online: status !== 'offline',
    lastSeen: serverTimestamp(),
  });
}

export function subscribePresence(uid: string, cb: (p: PresenceRecord | null) => void): Unsubscribe {
  return onValue(ref(getFirebaseDb(), `presence/${uid}`), (snap) => {
    if (!snap.exists()) {
      cb(null);
      return;
    }
    cb(snap.val() as PresenceRecord);
  });
}

export function subscribeAllAgentPresence(
  cb: (map: Record<string, PresenceRecord>) => void,
): Unsubscribe {
  return onValue(ref(getFirebaseDb(), 'presence'), (snap) => {
    const val = snap.val() as Record<string, PresenceRecord> | null;
    if (!val) {
      cb({});
      return;
    }
    const agents: Record<string, PresenceRecord> = {};
    for (const [uid, p] of Object.entries(val)) {
      if (p.role === 'agent' || p.role === 'admin') agents[uid] = p;
    }
    cb(agents);
  });
}

export function countOnlineAgents(presenceMap: Record<string, PresenceRecord>): number {
  return Object.values(presenceMap).filter((p) => p.online && p.status !== 'offline').length;
}
